import React from 'react';
import { X, Edit2, Calendar, User, Paperclip, Tag as TagIcon } from 'lucide-react';
import {
  getCategoryLabel,
  getCategoryColor,
  getPriorityLabel,
  getPriorityColor,
  formatFileSize,
  type Note
} from '../../../services/queries/noteQueries';
import { useToast } from '../../../contexts/ToastContext';
import { StandardButton } from '../../standard';

interface ViewNoteModalProps {
  note: Note | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
}

export const ViewNoteModal: React.FC<ViewNoteModalProps> = ({
  note,
  isOpen,
  onClose,
  onEdit
}) => {
  const { info } = useToast();

  if (!isOpen || !note) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">
              {note.title}
            </h2>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getCategoryColor(note.category)}`}>
                {getCategoryLabel(note.category)}
              </span>
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getPriorityColor(note.priority)}`}>
                {getPriorityLabel(note.priority)}
              </span>
              {note.isPinned && (
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 text-xs rounded font-medium">
                  Pinned
                </span>
              )}
              {note.isArchived && (
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 text-xs rounded font-medium">
                  Archived
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={onEdit}
              className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
              title="Edit Note"
            >
              <Edit2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Metadata */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Created</div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {new Date(note.createdDate).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">by {note.createdBy}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Last Modified</div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {new Date(note.lastModified).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">by {note.lastModifiedBy}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Record */}
            {note.relatedTo && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                      Related to {note.relatedTo.objectType}
                    </div>
                    <div className="text-sm text-blue-800 dark:text-blue-400">
                      {note.relatedTo.recordName}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tags */}
            {note.tags.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <TagIcon className="w-4 h-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Tags</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {note.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Note Content */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Content</h3>
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div
                  className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-purple-600 prose-strong:text-gray-900 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-li:text-gray-700"
                  dangerouslySetInnerHTML={{ __html: note.content }}
                />
              </div>
            </div>

            {/* Attachments */}
            {note.attachments.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Paperclip className="w-4 h-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Attachments ({note.attachments.length})
                  </h3>
                </div>
                <div className="space-y-2">
                  {note.attachments.map(attachment => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded">
                          <Paperclip className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {attachment.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatFileSize(attachment.size)} • Uploaded {new Date(attachment.uploadedDate).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <StandardButton
                        variant="link"
                        size="sm"
                        onClick={() => info('Coming Soon', 'Download functionality will be implemented in a future update')}
                      >
                        Download
                      </StandardButton>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-900">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Note ID: {note.id}
          </div>
          <div className="flex gap-3">
            <StandardButton
              variant="primary"
              onClick={onEdit}
            >
              Edit Note
            </StandardButton>
            <StandardButton
              variant="ghost"
              onClick={onClose}
            >
              Close
            </StandardButton>
          </div>
        </div>
      </div>
    </div>
  );
};
